import { useEffect, useState } from 'react'
import { api } from './api'
import type { ContentsEstimate } from './types'

export function useContentsEstimate(quantities: Record<string, number>) {
  const valid = Object.values(quantities).every(
    (n) => Number.isSafeInteger(n) && n >= 0,
  )
  const items = Object.entries(quantities)
    .filter(([, quantity]) => quantity > 0)
    .map(([categoryCode, quantity]) => ({ categoryCode, quantity }))
    .sort((a, b) => a.categoryCode.localeCompare(b.categoryCode))
  const key = valid && items.length ? JSON.stringify({ items }) : ''
  const [result, setResult] = useState<{
    key: string
    data: ContentsEstimate | null
    error: string
  }>({ key: '', data: null, error: '' })
  useEffect(() => {
    if (!key) return
    const controller = new AbortController()
    const timer = setTimeout(() => {
      api<ContentsEstimate>('/api/orders/estimate', {
        method: 'POST',
        body: key,
        signal: controller.signal,
      })
        .then((data) => {
          if (!controller.signal.aborted) setResult({ key, data, error: '' })
        })
        .catch((error) => {
          if (!controller.signal.aborted)
            setResult({
              key,
              data: null,
              error:
                error instanceof Error
                  ? error.message
                  : 'Could not calculate this package.',
            })
        })
    }, 150)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [key])
  return {
    estimate: key && result.key === key ? result.data : null,
    pending: !!key && result.key !== key,
    error: !valid
      ? 'Enter whole quantities of zero or more.'
      : result.key === key
        ? result.error
        : '',
  }
}
